import { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTicket, useApproveTicket, useRejectTicket } from '@/hooks/useTickets';
import { DashboardLayout } from '@/components/shared/DashboardLayout';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Textarea } from '@/components/ui/textarea';
import { StatusBadge, PriorityBadge } from '@/components/shared/StatusBadge';
import { SlaTimer } from '@/components/shared/SlaTimer';
import { LoadingSpinner } from '@/components/shared/LoadingStates';
import IssueDescription from '@/components/shared/IssueDescription';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { ArrowLeft, CheckCircle, XCircle } from '@phosphor-icons/react';
import { formatDateTime } from '@/lib/utils';

const REJECTION_REASONS = [
  'Invalid serial number',
  'Product not under warranty',
  'Duplicate ticket',
  'Insufficient information',
  'Service not covered',
  'Other',
];

export const TicketReview = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [showRejectDialog, setShowRejectDialog] = useState(false);
  const [rejectionReason, setRejectionReason] = useState('');
  const [rejectionNote, setRejectionNote] = useState('');

  const { data: ticket, isLoading } = useTicket(id);
  const approveTicket = useApproveTicket();
  const rejectTicket = useRejectTicket();

  const handleApprove = async () => {
    try {
      await approveTicket.mutateAsync(id);
      toast.success('Ticket approved successfully');
      navigate('/agent');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to approve ticket');
    }
  };

  const handleReject = async () => {
    if (!rejectionReason) {
      toast.error('Please select a rejection reason');
      return;
    }

    try {
      const reason = rejectionReason === 'Other' && rejectionNote 
        ? rejectionNote 
        : rejectionReason;
      await rejectTicket.mutateAsync({ id, reason });
      toast.success('Ticket rejected');
      navigate('/agent');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reject ticket');
    }
  };

  if (isLoading) return <LoadingSpinner fullScreen />;
  if (!ticket) return <div>Ticket not found</div>;

  return (
    <DashboardLayout role="agent">
      <div className="page-padding max-w-5xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <Button 
            variant="ghost" 
            onClick={() => navigate('/agent')}
            className="mb-4"
            data-testid="back-to-queue-btn"
          >
            <ArrowLeft size={20} className="mr-2" />
            Back to Queue
          </Button>
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div className="space-y-2 min-w-0">
              <h1 className="text-3xl sm:text-4xl font-black tracking-tight break-all">
                {ticket.ticket_number}
              </h1>
              <div className="flex items-center gap-2 flex-wrap">
                <StatusBadge status={ticket.status} />
                <PriorityBadge priority={ticket.priority} />
                <SlaTimer deadline={ticket.sla_deadline} status={ticket.status} />
              </div>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-6">
            {/* Ticket Details */}
            <Card className="p-6" data-testid="ticket-details-card">
              <h2 className="text-xl font-bold mb-4">Ticket Details</h2>
              <div className="space-y-4">
                <div>
                  <p className="label-micro mb-1">Customer</p>
                  <p className="font-semibold">{ticket.customer_name}</p>
                  <p className="text-sm text-muted-foreground">{ticket.customer_phone}</p>
                </div>
                <div>
                  <p className="label-micro mb-1">Product</p>
                  <p className="font-semibold">{ticket.product_name}</p>
                  <p className="text-sm text-muted-foreground">
                    Serial: {ticket.serial_number} • {ticket.product_category}
                  </p>
                </div>
                <div>
                  <p className="label-micro mb-1">Warranty Status</p>
                  <span className={`px-2 py-1 rounded-sm text-xs font-semibold ${
                    ticket.warranty_status === 'Active' 
                      ? 'bg-green-100 text-green-800' 
                      : 'bg-red-100 text-red-800'
                  }`}>
                    {ticket.warranty_status}
                  </span>
                </div>
                <div>
                  <p className="label-micro mb-1">Issue Category</p>
                  <p className="font-medium">{ticket.issue_category}</p>
                </div>
                <div>
                  <p className="label-micro mb-1">Issue Description</p>
                  <IssueDescription description={ticket.description} />
                </div>
                <div>
                  <p className="label-micro mb-1">Submitted</p>
                  <p className="text-sm">{formatDateTime(ticket.created_at)}</p>
                </div>
              </div>
            </Card>

            {/* Attachments */}
            {ticket.attachments?.length > 0 && (
              <Card className="p-6" data-testid="attachments-card">
                <h2 className="text-xl font-bold mb-4">Attachments</h2>
                <div className="grid grid-cols-2 gap-4">
                  {ticket.attachments.map((attachment, idx) => (
                    <img
                      key={idx}
                      src={attachment.url}
                      alt={`Attachment ${idx + 1}`}
                      className="rounded-sm border border-border"
                    />
                  ))}
                </div>
              </Card>
            )}
          </div>

          {/* Sidebar - Actions */}
          <div className="space-y-6">
            <Card className="p-6" data-testid="actions-card">
              <h3 className="font-bold mb-4">Actions</h3>
              <div className="space-y-3">
                <Button 
                  onClick={handleApprove}
                  disabled={approveTicket.isPending}
                  className="w-full"
                  data-testid="approve-btn"
                >
                  <CheckCircle size={20} className="mr-2" />
                  {approveTicket.isPending ? 'Approving...' : 'Approve Ticket'}
                </Button>
                <Button 
                  onClick={() => setShowRejectDialog(true)}
                  variant="destructive"
                  className="w-full"
                  data-testid="reject-btn"
                >
                  <XCircle size={20} className="mr-2" />
                  Reject Ticket
                </Button>
              </div>
            </Card>

            {/* Verification Checklist */}
            <Card className="p-6 bg-blue-50 border-blue-200" data-testid="verification-card">
              <h3 className="font-bold mb-3">Verification Checklist</h3>
              <div className="space-y-2 text-sm">
                <label className="flex items-center gap-2">
                  <input type="checkbox" className="rounded" />
                  Serial number is valid
                </label>
                <label className="flex items-center gap-2">
                  <input type="checkbox" className="rounded" />
                  Product is under warranty
                </label>
                <label className="flex items-center gap-2">
                  <input type="checkbox" className="rounded" />
                  Issue description is clear
                </label>
                <label className="flex items-center gap-2">
                  <input type="checkbox" className="rounded" />
                  No duplicate tickets found
                </label>
              </div>
            </Card>
          </div>
        </div>

        {/* Reject Dialog */}
        <Dialog open={showRejectDialog} onOpenChange={setShowRejectDialog}>
          <DialogContent data-testid="reject-dialog">
            <DialogHeader>
              <DialogTitle>Reject Ticket</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div>
                <Label>Rejection Reason</Label>
                <Select value={rejectionReason} onValueChange={setRejectionReason}>
                  <SelectTrigger data-testid="rejection-reason-select">
                    <SelectValue placeholder="Select a reason" />
                  </SelectTrigger>
                  <SelectContent>
                    {REJECTION_REASONS.map((reason) => (
                      <SelectItem key={reason} value={reason}>{reason}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              {rejectionReason === 'Other' && (
                <div>
                  <Label>Additional Details</Label>
                  <Textarea
                    value={rejectionNote}
                    onChange={(e) => setRejectionNote(e.target.value)}
                    placeholder="Please provide more details..."
                    data-testid="rejection-note-input"
                  />
                </div>
              )}
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowRejectDialog(false)}>
                Cancel
              </Button>
              <Button 
                variant="destructive" 
                onClick={handleReject}
                disabled={rejectTicket.isPending}
                data-testid="confirm-reject-btn"
              >
                {rejectTicket.isPending ? 'Rejecting...' : 'Confirm Rejection'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
};